import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { ProblemData, ROMAN_MAX_OPTIONS, RomanQuestionOptions } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { withThousands } from "./PlaceValueGenerator.ts";

/**
 * Roman numerals, converted in either direction. See ADR 0079.
 *
 * Every value has exactly one numeral here: the subtractive form (4 is IV, 9 is
 * IX, 40 is XL, 90 is XC, 400 is CD, 900 is CM), never IIII or VIIII. Some
 * clock faces write IIII, but a key with two defensible answers for one prompt
 * is what ADR 0022 §1 test 4 refuses. The same rule keeps a letter from running
 * more than three in a row, which is the mistake a student is most likely to
 * make and the one the key must not commit.
 *
 * Nothing is drawn: a prompt and its answer are short strings, so the format
 * prints through the place value renderer.
 */

/** The numerals in descending order, subtractive pairs included. */
const NUMERALS: ReadonlyArray<readonly [number, string]> = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
];

/** The largest value with a standard numeral (MMMCMXCIX). */
export const LARGEST_ROMAN = 3999;

/** 1994 -> "MCMXCIV". Defined for 1 to 3999 only. */
export function toRoman(value: number): string {
    if (!Number.isInteger(value) || value < 1 || value > LARGEST_ROMAN) {
        throw new RangeError(`${value} has no standard Roman numeral`);
    }
    let rest = value;
    let out = "";
    for (const [amount, letters] of NUMERALS) {
        while (rest >= amount) {
            out += letters;
            rest -= amount;
        }
    }
    return out;
}

/**
 * "MCMXCIV" -> 1994, or null when the text is not a standard numeral.
 *
 * Standard means `toRoman` would have written it, so the test is a round trip:
 * IIII, VX, IC and MMMM all read as a number by a naive sum and are all refused.
 */
export function fromRoman(text: string): number | null {
    const values: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
    if (!/^[IVXLCDM]+$/.test(text)) {
        return null;
    }
    let total = 0;
    for (let i = 0; i < text.length; i++) {
        const here = values[text[i]];
        const next = values[text[i + 1]] ?? 0;
        total += here < next ? -here : here;
    }
    if (total < 1 || total > LARGEST_ROMAN || toRoman(total) !== text) {
        return null;
    }
    return total;
}

/** A setting outside the offered values falls back to the default, not to nonsense. */
function usableMax(max: number): number {
    return ROMAN_MAX_OPTIONS.includes(max) ? max : 100;
}

function questionTypes(question: RomanQuestionOptions): RomanQuestionOptions[] {
    if (question === RomanQuestionOptions.Mixed) {
        return [RomanQuestionOptions.ToRoman, RomanQuestionOptions.ToNumber];
    }
    return [question];
}

/**
 * One Roman numeral question. Always succeeds.
 *
 * `display` is `[prompt, answer]`; `numbers` carries the value so the
 * de-duplication guard can key on it.
 */
export function buildRomanProblem(
    question: RomanQuestionOptions,
    max: number,
    key: number
): ProblemData {
    const value = getRandomInt(1, usableMax(max));
    const numeral = toRoman(value);
    const number = withThousands(value);
    const display =
        question === RomanQuestionOptions.ToNumber
            ? [`${numeral} =`, number]
            : [`${number} =`, numeral];
    return { key, numbers: [value], operator: "", display };
}

/**
 * How many different values a sheet can ask about at this range.
 *
 * Read by `perPageOptions`: a ten-value range cannot fill a page of sixteen
 * without repeating, which is a limit on how many questions exist and not on
 * space, so a page measurement would never find it.
 */
export function distinctRomanValues(max: number): number {
    return usableMax(max);
}

/** A page-shaped set of Roman numeral problems. */
export function generateRomanPages(settings: ISettingsProps): ProblemData[][] | null {
    const types = questionTypes(settings.RomanQuestion ?? RomanQuestionOptions.Mixed);
    const max = settings.RomanMax ?? 100;
    return generateProblemPages({
        settings,
        choices: types,
        build: (question, key) => buildRomanProblem(question, max, key),
        // By value, not by prompt: a Mixed sheet that asked "14 =" and also
        // "XIV =" would hand the student the answer to one in the other.
        fingerprint: (problem) => String(problem.numbers[0]),
    });
}
